import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';

function OnlineUsers() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [onlineData, setOnlineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // all, admin, student
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchOnlineUsers();
    
    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchOnlineUsers();
      }, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchOnlineUsers = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getOnlineUsers();
      setOnlineData(response.data);
    } catch (err) {
      showToast('Failed to load online users', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatLastActive = (date) => {
    const now = new Date();
    const lastActive = new Date(date);
    const diffMs = now - lastActive;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 5) return `${diffMins} minutes ago`;
    return lastActive.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAllUsers = () => {
    if (!onlineData) return [];
    return [...onlineData.admins.users, ...onlineData.students.users];
  };

  const getFilteredUsers = () => {
    if (!onlineData) return [];
    
    let users = [];
    if (filterRole === 'all') {
      users = getAllUsers();
    } else if (filterRole === 'admin') {
      users = onlineData.admins.users;
    } else {
      users = onlineData.students.users;
    }
    
    // Apply search filter
    if (searchTerm) {
      users = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return users;
  };

  const getRoleBadgeColor = (role) => {
    return role === 'admin' ? '#667eea' : '#48bb78';
  };

  if (loading && !onlineData) {
    return (
      <div className="loading">Loading online users...</div>
    );
  }

  const filteredUsers = getFilteredUsers();

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/admin')}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to Dashboard
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
                🟢 Online Users
              </h2>
              <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
                Users active in the last 5 minutes
              </p>
            </div>
            <button
              onClick={fetchOnlineUsers}
              className="btn btn-primary"
              style={{ fontSize: '13px', padding: '8px 16px' }}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {onlineData && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              backgroundColor: '#f7fafc',
              padding: '16px',
              borderRadius: '10px',
              border: '1.5px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Total Online</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748' }}>
                {onlineData.total}
              </div>
            </div>
            <div style={{
              backgroundColor: '#eef2ff',
              padding: '16px',
              borderRadius: '10px',
              border: '1.5px solid #c7d2fe'
            }}>
              <div style={{ fontSize: '12px', color: '#667eea', marginBottom: '4px' }}>Admins</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#667eea' }}>
                {onlineData.admins.count}
              </div>
            </div>
            <div style={{
              backgroundColor: '#f0fdf4',
              padding: '16px',
              borderRadius: '10px',
              border: '1.5px solid #86efac'
            }}>
              <div style={{ fontSize: '12px', color: '#48bb78', marginBottom: '4px' }}>Students</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#48bb78' }}>
                {onlineData.students.count}
              </div>
            </div>
          </div>
        )}

        {/* Auto-refresh toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <input
            type="checkbox"
            id="autoRefresh"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="autoRefresh" style={{ fontSize: '13px', color: '#4a5568', cursor: 'pointer' }}>
            Auto-refresh every 30 seconds
          </label>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {/* Search */}
          <input
            type="text"
            placeholder="🔍 Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: '250px',
              padding: '10px 14px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
          
          {/* Role filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Users</option>
            <option value="admin">Admins Only</option>
            <option value="student">Students Only</option>
          </select>
        </div>

        {/* Users List */}
        {filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#718096', fontSize: '14px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>😴</div>
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>
              {searchTerm || filterRole !== 'all' 
                ? 'No users found matching your filters' 
                : 'No users are currently online'}
            </p>
            <p style={{ fontSize: '13px', color: '#a0aec0' }}>
              {searchTerm || filterRole !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Users will appear here when they become active'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredUsers.map((user) => (
              <div key={user._id} style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '16px',
                border: '1.5px solid #e2e8f0',
                transition: 'all 0.2s',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px'
              }}>
                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '6px'
                  }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)'
                    }} />
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#2d3748',
                      margin: 0
                    }}>
                      {user.name}
                    </h3>
                    <span style={{
                      padding: '3px 10px',
                      backgroundColor: getRoleBadgeColor(user.role) + '20',
                      color: getRoleBadgeColor(user.role),
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {user.role}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#718096',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span>📧 {user.email}</span>
                  </div>
                </div>

                {/* Last Active */}
                <div style={{
                  textAlign: 'right',
                  fontSize: '12px',
                  color: '#718096'
                }}>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>Last Active</div>
                  <div style={{ color: '#10b981', fontWeight: '600' }}>
                    {formatLastActive(user.lastActive)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer note */}
        <div style={{
          marginTop: '24px',
          padding: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#92400e'
        }}>
          💡 <strong>Note:</strong> Users are considered online if they've been active within the last 5 minutes.
        </div>
      </div>
    </div>
  );
}

export default OnlineUsers;
